import { createContext, useContext, useState } from "react";
import type { Document } from "@/types/models";

export interface OpenDocumentOptions {
    doc: Document;
    page?: string;
    sourceText?: string;
    side?: "left" | "right";
    showTabs?: boolean;
    hideOverlay?: boolean;
}

interface DocumentViewerState {
    viewingDoc: Document | null;
    viewingPage: string;
    sourceText?: string;
    side: "left" | "right";
    showTabs: boolean;
    hideOverlay: boolean;
}

interface DocumentViewerContextValue extends DocumentViewerState {
    openDocument: (options: OpenDocumentOptions) => void;
    closeDocument: () => void;
}

const DocumentViewerContext = createContext<DocumentViewerContextValue | null>(null);

const defaultState: DocumentViewerState = {
    viewingDoc: null,
    viewingPage: "",
    sourceText: undefined,
    side: "left",
    showTabs: false,
    hideOverlay: false,
};

export function DocumentViewerProvider({ children }: { children: React.ReactNode }) {
    const [state, setState] = useState<DocumentViewerState>(defaultState);

    const openDocument = ({ doc, page = "", sourceText, side = "left", showTabs = false, hideOverlay = false }: OpenDocumentOptions) => {
        setState({ viewingDoc: doc, viewingPage: page, sourceText, side, showTabs, hideOverlay });
    };

    const closeDocument = () => {
        setState(prev => ({ ...defaultState, side: prev.side }));
    };

    return (
        <DocumentViewerContext.Provider value={{ ...state, openDocument, closeDocument }}>
            {children}
        </DocumentViewerContext.Provider>
    );
}

export function useDocumentViewer() {
    const ctx = useContext(DocumentViewerContext);
    if (!ctx) throw new Error("useDocumentViewer must be used within a DocumentViewerProvider");
    return ctx;
}
